import { unref } from 'vue'

import {
  ADDON_TYPES,
  ADDON_PRICE_FREQUENCY,
  PRODUCT_TYPE_IX,
  PRODUCT_TYPE_LAG,
  PRODUCT_TYPE_MCR2,
  PRODUCT_TYPE_MEGAPORT,
  PRODUCT_TYPE_MVE,
  PRODUCT_TYPE_VXC,
} from '@/Globals.js'

const TRANSIT_VXC_MIN_SPEED = 20
const DEFAULT_VXC_MIN_SPEED = 1

// TODO: Split this file into smaller files inside a 'utilities/functions' folder like we do in CMA.
// Can be done under ENG-24845.

/**
 * Determine the minimum VXC speed
 * @param {object} bEnd The B-End of the connection.
 * @returns {number}
 */
export function determineMinVxcSpeed(bEnd) {
  const isTransit = bEnd?.connectType === 'TRANSIT'

  return isTransit ? TRANSIT_VXC_MIN_SPEED : DEFAULT_VXC_MIN_SPEED
}

/**
 * Determine the maximum VXC speed
 * @param {object} aEnd The A-End access service.
 * @param {object} bEnd The B-End of the connection.
 * @returns {number}
 */
export function determineMaxVxcSpeed(aEnd, bEnd) {
  // NOTE: The check for `maxVxcSpeed` on the A-End makes no sense at present. This field is returned by NetAuto and our A-End access services are always configured in-app.
  let maxSpeedA = aEnd?.maxVxcSpeed || aEnd?._aggSpeed || Number.MAX_SAFE_INTEGER
  let maxSpeedB = bEnd?.maxVxcSpeed || bEnd?._aggSpeed || Number.MAX_SAFE_INTEGER

  return Math.min(maxSpeedA, maxSpeedB)
}

/**
 * Determine the max speed for IX connections
 * @param {object} aEnd The A-End access service. Always a Megaport port at this point.
 * @param {object} bEnd The B-End of the connection, which always is an IX type.
 * @returns {number} The maximum speed for the IX connection in Mbps.
 * NOTE: This function serves no purpose at the moment. I assume it is here as a copy-paste result from the Portal.
 * At present, all access services in the Quoting Tool are configured in-app, so a field like `maxVxcSpeed`, which is returned by NetAuto, would never be present in the A-End.
 * Only leaving it to avoid out-of-scope changes and because we are planning to add the ability to import existing deployed services to the Quoting Tool in the near future.
 */
export function determineMaxIxSpeed(aEnd, bEnd) {
  // Local IX
  if (aEnd && bEnd && aEnd._location && aEnd._location.metro === bEnd.group_metro) {
    return aEnd._aggSpeed
  }

  // For remote IX use maxVxcSpeed if we have it, otherwise fallback to _aggSpeed.
  return aEnd.maxVxcSpeed || aEnd._aggSpeed
}

/**
 * Converts a speed value to a human readable format.
 * @param {number} speed The speed value in Mbps.
 * @returns {string} The converted speed value.
 */
export const convertSpeed = function (speed) {
  if (!speed) return '-'
  if (speed >= 1000) return `${speed / 1000} Gbps`
  return `${speed} Mbps`
}

/**
 * Converts a product type constant to a human readable format.
 * @param {'MEGAPORT' | 'MCR2' | 'MVE' | 'VXC' | 'IX' | 'LAG'} productType The product type constant.
 * @returns {string} The human readable product type.
 */
export const convertProductType = productType => {
  switch (productType) {
    case PRODUCT_TYPE_MEGAPORT:
      return window.mpApp.$t('productNames.port')
    case PRODUCT_TYPE_MCR2:
      return window.mpApp.$t('productNames.mcr')
    case PRODUCT_TYPE_MVE:
      return window.mpApp.$t('productNames.mve')
    case PRODUCT_TYPE_VXC:
      return window.mpApp.$t('productNames.vxc')
    case PRODUCT_TYPE_IX:
      return window.mpApp.$t('productNames.ix')
    case PRODUCT_TYPE_LAG:
      return window.mpApp.$t('productNames.lag')
    default:
      return productType
  }
}

/**
 * Gets the product icon name by product type.
 * @param {'MEGAPORT' | 'MCR2' | 'MVE' | 'VXC' | 'IX' | 'LAG'} type The product type constant.
 * @returns {string} The product icon name.
 */
export function getProductIconByType(type) {
  switch (type) {
    case PRODUCT_TYPE_MEGAPORT:
      return 'MegaportIcon'
    case PRODUCT_TYPE_MCR2:
      return 'McrIcon'
    case PRODUCT_TYPE_MVE:
      return 'MveIcon'
    case PRODUCT_TYPE_VXC:
      return 'VxcIcon'
    case PRODUCT_TYPE_IX:
      return 'IxIcon'
    case PRODUCT_TYPE_LAG:
      return 'LagIcon'
    default:
      return ''
  }
}

/**
 * Capitalises the first letter of a given string while lowercasing the rest.
 * @param {string} string The string to capitalise.
 * @returns {string} The capitalised string.
 */
export const capitalizeFirstOnly = string => {
  if (!string) return ''

  return `${string.charAt(0).toLocaleUpperCase()}${string.slice(1).toLocaleLowerCase()}`
}

/**
 * Converts a string from snake_case to Title Case.
 * @param {string} string The string to convert.
 * @returns {string} The converted string.
 */
export const snakeCaseToTitleCase = string => {
  return string.split('_').map(capitalizeFirstOnly).join(' ')
}

/**
 * Implementation of `Promise.withResolvers()` which is not yet supported with the Node.js version used in this app (needs v22+).
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/withResolvers Promise.withResolvers}
 * @returns {object} An object containing a new promise and it's resolve/reject functions.
 */
export const promiseWithResolvers = () => {
  let resolve, reject

  const promise = new Promise((res, rej) => {
    resolve = res
    reject = rej
  })

  return { promise, resolve, reject }
}

/**
 * Formats a currency amount according to the JS Intl.NumberFormat standard.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat Intl.NumberFormat}
 * @param {number} value The monetary value to format.
 * @param {string} currency The currency to use for formatting.
 * @returns {string} The formatted currency amount.
 */
export const formatCurrencyAmount = (value, currency) => {
  const standardCurrency = (currency || 'USD').toUpperCase()

  // When undefined is passed as locale, the currency will be displayed as per the browser location,
  // i.e. if the browser location is Germany, the amount will be displayed as '123.456,79 €',
  // whereas if the location is any English speaking country like Australia, it will be displayed as '€123,456.79'.
  let currencyValue = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: standardCurrency,
    currencyDisplay: 'narrowSymbol',
  }).format(value)

  if (!value) {
    currencyValue = currencyValue.replace('NaN', ' -')
  }

  return `${currencyValue} ${standardCurrency}`
}

Array.prototype.unique = function () {
  return this.reduce(function (accum, current) {
    if (accum.indexOf(current) < 0) {
      accum.push(current)
    }
    return accum
  }, [])
}

/**
 * Copies a DOM node and it's children to the clipboard.
 * @param {HTMLElement} node The node to copy.
 * @param {boolean} withStyling Whether to include styles in the copied text. Defaults to true.
 */
export const copyNodeToClipboard = (node, withStyling = true) => {
  let success

  if (withStyling) {
    // Clear the existing selection
    window.getSelection().removeAllRanges()

    // Select the node to be copied
    const range = document.createRange()
    range.selectNode(node)
    window.getSelection().addRange(range)

    // I know this is flagged as deprecated, but Firefox straight up does not support the
    // new standard for copying to the clipboard, so we must use the old deprecated system
    success = document.execCommand('copy')

    // Clear the node selection
    window.getSelection().removeAllRanges()
  } else {
    // Use an event listener to intercept the copy and manually insert the styleless HTML
    const removeStyling = event => {
      event.clipboardData.setData('text/html', node.innerHTML)
      event.clipboardData.setData('text/plain', node.innerHTML)
      event.preventDefault()
    }
    document.addEventListener('copy', removeStyling)
    success = document.execCommand('copy')
    document.removeEventListener('copy', removeStyling)
  }

  if (success) {
    window.mpApp.$notify.success({
      title: window.mpApp.$t('general.copy-to-clipboard'),
      message: window.mpApp.$t('general.copied-to-clipboard', { target: window.mpApp.$t('general.network-design') }),
    })
  } else {
    window.mpApp.$notify.error({
      title: window.mpApp.$t('general.copy-to-clipboard'),
      message: window.mpApp.$t('general.copy-failed'),
    })
  }
}

/**
 * Set fallback image if src image does not exist and does not already have a fallback image
 * @param {Object} $event Event argument
 * @param {String} fallbackImage Fallback image URL
 */
export const setFallbackImage = ($event, fallbackImage) => {
  if ($event.target.src !== fallbackImage) $event.target.src = fallbackImage
}

/**
 * Formats the service term to a human readable string.
 * @param {number} term The length of the term for a service.
 * @param {boolean} includeTermSuffix Whether to include the word 'Term' as a suffix. Defaults to false.
 * @returns {string} The formatted service term.
 */
export const getTermText = (term, includeTermSuffix = false) => {
  let termText
  if (term === 1) {
    termText = window.mpApp.$t('general.monthly-term')
  } else {
    termText = `${window.mpApp.$tc('general.count-months', term, { count: term })}`
  }

  if (includeTermSuffix) {
    termText = `${termText} ${window.mpApp.$t('general.term')}`
  }
  return termText
}

/**
 * Extracts the environment name from the public API URL
 * @param {string} baseUrl public API URL
 * @returns {string} environment name
 */
function extractEnvironmentNameFromPublicApi(baseUrl) {
  const publicApiRegExp = /api-(.+?)\.megaport\.com/
  const matchArray = baseUrl.match(publicApiRegExp)

  return matchArray[1]
}

/**
 * Determines the Client Portal URL
 * @param {string} apiEnvironmentName API environment name
 * @returns {string} Client Portal URL
 */
function buildPortalUrl(apiEnvironmentName) {
  const { protocol, host } = window.location

  return host.startsWith('localhost')
    ? `https://portal-${apiEnvironmentName}.megaport.com`
    : `${protocol}//${host.replace('pricing', 'portal')}`
}

/**
 * Augments the window object with the provided extra properties
 * @param {Object} extraProperties An object containing the extra properties to be added to the window object
 */
function augmentWindowObject(extraProperties) {
  Object.entries(extraProperties).forEach(([key, value]) => {
    window[key] = value
  })
}

/**
 * Builds the app configuration object to be used throughout the app
 * @param {Object} environmentConfiguration The configuration defined in the environment.js file
 * @returns {Object} An augmented version of the environmentConfiguration object
 */
export function buildAppConfiguration(environmentConfiguration) {
  const isProduction = environmentConfiguration.environmentName === 'production'

  const apiEnvironmentName = isProduction
    ? environmentConfiguration.environmentName
    : extractEnvironmentNameFromPublicApi(environmentConfiguration.publicApi)

  const clientPortalUrl = buildPortalUrl(apiEnvironmentName)

  augmentWindowObject({ apiEnvironmentName, isProduction, clientPortalUrl })

  return Object.freeze({ ...environmentConfiguration, apiEnvironmentName, isProduction, clientPortalUrl })
}

/**
 * Helper function to normalise values, refs, and getters to values.
 * NOTE: This is temporary until the app is migrated to Vue 3.
 * TODO: Remove this function once the app is migrated to Vue 3.
 * @param {any} value - The value to normalise. This can be a value, ref, or getter.
 */
export function internalToValue(value) {
  return typeof value === 'function' ? value() : unref(value)
}

/**
 * The prices array from the pricebook API response.
 * @typedef {Object} PriceDetails
 * @property {string} addOnType The type of the add-on.
 * @property {'once' | 'monthly'} frequency The frequency of the charge.
 * @property {number} amount The charge amount.
 */

/**
 * Gets the charge amount for a specific add-on type from the price details.
 * @param {PriceDetails} priceDetails The prices array from the pricebook API response.
 * @param {boolean} isRecurring Whether the charge is recurring or not.
 * @param {string} addOnType The type of add-on to retrieve the charge for, defaults to CROSS_CONNECT.
 */
export const getAddOnCharge = (priceDetails, isRecurring, addOnType = ADDON_TYPES.CROSS_CONNECT) => {
  const priceFrequency = isRecurring ? ADDON_PRICE_FREQUENCY.MONTHLY : ADDON_PRICE_FREQUENCY.ONCE
  const priceObj = priceDetails?.find(price => price.addOnType === addOnType && price.frequency === priceFrequency)

  return priceObj?.amount
}
